/**
 * EventDecorations — Shared decorative SVG layer for event identity.
 *
 * Renders event-type-specific decorative motifs on top of the event card background.
 * Used by CustomerDashboard hero carousel and EventDetail hero summary.
 *
 * MOTIF GUIDE:
 *   Wedding     → interlocking rings + gold sparkle accents
 *   Engagement  → prominent ring with diamond + soft sparkles
 *   Anniversary → champagne flute pair (cheers pose) + bubble sparkles
 *   Graduation  → mortarboard cap + purple/gold confetti
 *   Birthday    → gold/green balloons + classic confetti
 *   Baby Shower → pastel balloon cluster + soft dots
 *   Housewarming→ house silhouette + warm dots
 *   Holiday     → glow orbs + mixed confetti
 */

import React, { useMemo } from 'react';

// CSS keyframes for living sparkle effect (Home cards only)
const sparkleCSS = `
@keyframes sparkle-drift {
  0%, 100% { opacity: var(--sp-base); transform: translateY(0) scale(1); }
  50% { opacity: var(--sp-peak); transform: translateY(-3px) scale(1.05); }
}
`;
let sparkleStyleInjected = false;

export function EventDecorations({ decoVariant, decoFlags, eventId, surface = 'event' }) {
  const isHome = surface === 'home';

  // Deterministic random selection: pick 2-3 sparkle indices per card.
  // Hooks MUST be called unconditionally on every render (Rules of Hooks),
  // so this useMemo sits ABOVE the early-return guard below. The guard
  // then short-circuits rendering when the variant/flags aren't ready,
  // without changing visual behaviour.
  const animatedIndices = useMemo(() => {
    if (!isHome) return new Set();
    let hash = 0;
    for (let i = 0; i < (eventId || '').length; i++) {
      hash = ((hash << 5) - hash + (eventId || '').charCodeAt(i)) | 0;
    }
    const count = 2 + (Math.abs(hash) % 2); // 2 or 3
    const indices = new Set();
    for (let i = 0; i < count; i++) {
      indices.add(Math.abs((hash * (i + 7)) % 7));
    }
    return indices;
  }, [eventId, isHome]);

  if (!decoVariant || !decoFlags) return null;

  // Inject sparkle CSS once
  if (isHome && !sparkleStyleInjected && typeof document !== 'undefined') {
    const style = document.createElement('style');
    style.textContent = sparkleCSS;
    document.head.appendChild(style);
    sparkleStyleInjected = true;
  }

  // Returns style props for an animated sparkle circle (Home only)
  const sparkleProps = (index, baseOpacity) => {
    if (!isHome || !animatedIndices.has(index)) return {};
    const delay = (index * 1.3) % 6;
    return {
      style: {
        '--sp-base': `${Math.max(baseOpacity * 0.5, 0.10)}`,
        '--sp-peak': `${Math.min(baseOpacity * 1.2, 0.40)}`,
        animation: `sparkle-drift ${4 + (index % 3)}s ease-in-out ${delay}s infinite`,
      },
    };
  };

  // Home motif: mid-right safe zone, 65% scale, slightly stronger presence
  const homeMotifStyle = (eventStyles) => ({
    ...eventStyles,
    top: '50%',
    right: '8px',
    bottom: undefined,
    left: undefined,
    transform: 'translateY(-50%) scale(0.65)',
    opacity: Math.min((eventStyles.opacity || 0.5) * 0.7, 0.35),
    filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.08))',
  });
  // Home motif for left-anchored elements (balloons)
  const homeMotifLeftStyle = (eventStyles) => ({
    ...eventStyles,
    transform: 'scale(0.65)',
    transformOrigin: 'top left',
    opacity: (eventStyles.opacity || 0.5) * 0.65,
    filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.08))',
  });

  const { dIsW, dIsA, dIsE, dIsBs, dIsGr, dIsHw, dIsHol, dIsClassic, dGoldOp, dGreenOp, dGreen2Op, dConfetti } = decoFlags;

  // Birthday-only classic (balloons + confetti, no rings/champagne)
  const dIsBirthday = dIsClassic && !dIsW && !dIsA;

  return (
    <>
      {/* ══════════════════════════════════════════════════════════
          WEDDING — Interlocking rings + gold sparkle scatter
          ══════════════════════════════════════════════════════════ */}
      {dIsW && (
        <>
          {/* Soft radial glow */}
          <div className="absolute pointer-events-none rounded-full" style={{
            top: '2%', right: '-6%', width: '60%', height: '80%',
            background: 'radial-gradient(ellipse at 55% 40%, rgba(220,195,130,0.10) 0%, transparent 50%)',
          }} />
          {/* Interlocking rings — primary motif */}
          <svg className="absolute pointer-events-none" style={isHome
            ? homeMotifStyle({ top: '20%', right: '8px', width: '100px', height: '70px', opacity: 0.40 })
            : { top: '20%', right: '8px', width: '100px', height: '70px', opacity: 0.40 }
          } viewBox="0 0 100 70">
            {/* Left ring — band with inner/outer edge for metallic depth */}
            <ellipse cx="36" cy="35" rx="22" ry="22" stroke="#C9AD6A" strokeWidth="4" fill="none" />
            <ellipse cx="36" cy="35" rx="19.5" ry="19.5" stroke="#E8D8A0" strokeWidth="0.6" fill="none" opacity="0.45" />
            {/* Right ring — interlocking band */}
            <ellipse cx="64" cy="35" rx="22" ry="22" stroke="#B8986A" strokeWidth="4" fill="none" />
            <ellipse cx="64" cy="35" rx="19.5" ry="19.5" stroke="#E8D8A0" strokeWidth="0.6" fill="none" opacity="0.45" />
            {/* Metallic highlight arcs — light catch on upper-left of each band */}
            <path d="M17 26 Q22 16 32 18" stroke="#F0E4C0" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.55" />
            <path d="M78 26 Q73 16 63 18" stroke="#F0E4C0" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.55" />
          </svg>
          {/* Gold sparkle scatter */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 300 220" preserveAspectRatio="none">
            <circle cx="215" cy="28" r="2" fill="#C9AD6A" opacity="0.50" {...sparkleProps(0, 0.50)} />
            <circle cx="245" cy="68" r="2.5" fill="#D4BD7A" opacity="0.45" {...sparkleProps(1, 0.45)} />
            <circle cx="268" cy="42" r="1.8" fill="#C9AD6A" opacity="0.42" {...sparkleProps(2, 0.42)} />
            <circle cx="230" cy="105" r="2" fill="#D4BD7A" opacity="0.38" {...sparkleProps(3, 0.38)} />
            <circle cx="260" cy="135" r="2.2" fill="#C9AD6A" opacity="0.35" {...sparkleProps(4, 0.35)} />
            <circle cx="200" cy="150" r="1.8" fill="#D4BD7A" opacity="0.30" {...sparkleProps(5, 0.30)} />
            <rect x="222" y="55" width="4" height="4" fill="#C9AD6A" opacity="0.40" transform="rotate(45 224 57)" {...sparkleProps(6, 0.40)} />
            <rect x="255" y="100" width="3.5" height="3.5" fill="#D4BD7A" opacity="0.35" transform="rotate(45 257 102)" />
            {/* Small interlocking rings echo — bottom corner */}
            <ellipse cx="248" cy="175" rx="8" ry="8" stroke="#C9AD6A" strokeWidth="1" fill="none" opacity="0.18" />
            <ellipse cx="260" cy="175" rx="8" ry="8" stroke="#B8986A" strokeWidth="1" fill="none" opacity="0.18" />
          </svg>
        </>
      )}

      {/* ══════════════════════════════════════════════════════════
          ENGAGEMENT — Prominent ring with diamond + sparkle halo
          ══════════════════════════════════════════════════════════ */}
      {dIsE && (
        <>
          {/* Soft blush glow */}
          <div className="absolute pointer-events-none rounded-full" style={{
            top: '4%', right: '-4%', width: '55%', height: '75%',
            background: 'radial-gradient(ellipse at 55% 38%, rgba(212,168,148,0.12) 0%, transparent 50%)',
          }} />
          {/* Ring with diamond — primary motif */}
          <svg className="absolute pointer-events-none" style={isHome
            ? homeMotifStyle({ top: '18%', right: '6px', width: '80px', height: '90px', opacity: 0.55 })
            : { top: '18%', right: '6px', width: '80px', height: '90px', opacity: 0.55 }
          } viewBox="0 0 80 90">
            {/* Ring band */}
            <ellipse cx="40" cy="52" rx="26" ry="28" stroke="#C4A090" strokeWidth="3" fill="none" />
            {/* Ring band highlight */}
            <path d="M18 40 Q22 30 32 32" stroke="#E0C8B8" strokeWidth="1.5" fill="none" strokeLinecap="round" opacity="0.65" />
            {/* Diamond setting — prong mount */}
            <line x1="34" y1="26" x2="38" y2="18" stroke="#C4A090" strokeWidth="1.5" opacity="0.6" />
            <line x1="46" y1="26" x2="42" y2="18" stroke="#C4A090" strokeWidth="1.5" opacity="0.6" />
            {/* Diamond */}
            <polygon points="40,10 48,18 40,28 32,18" fill="#D4BD7A" opacity="0.75" />
            <polygon points="40,10 48,18 40,16" fill="#E8D8A0" opacity="0.50" />
            {/* Diamond sparkle rays */}
            <line x1="40" y1="5" x2="40" y2="1" stroke="#D4BD7A" strokeWidth="1" opacity="0.55" strokeLinecap="round" />
            <line x1="50" y1="12" x2="54" y2="9" stroke="#D4BD7A" strokeWidth="1" opacity="0.45" strokeLinecap="round" />
            <line x1="30" y1="12" x2="26" y2="9" stroke="#D4BD7A" strokeWidth="1" opacity="0.45" strokeLinecap="round" />
          </svg>
          {/* Sparkle scatter — blush/gold */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 300 220" preserveAspectRatio="none">
            <circle cx="210" cy="30" r="2" fill="#D4A894" opacity="0.42" {...sparkleProps(0, 0.42)} />
            <circle cx="248" cy="72" r="2.2" fill="#D4BD7A" opacity="0.40" {...sparkleProps(1, 0.40)} />
            <circle cx="265" cy="45" r="1.8" fill="#C49584" opacity="0.38" {...sparkleProps(2, 0.38)} />
            <circle cx="225" cy="110" r="2" fill="#D4A894" opacity="0.35" {...sparkleProps(3, 0.35)} />
            <circle cx="258" cy="140" r="2.5" fill="#D4BD7A" opacity="0.32" {...sparkleProps(4, 0.32)} />
            <circle cx="195" cy="155" r="1.8" fill="#C49584" opacity="0.28" {...sparkleProps(5, 0.28)} />
            <rect x="235" y="58" width="4" height="4" fill="#D4A894" opacity="0.35" transform="rotate(45 237 60)" {...sparkleProps(6, 0.35)} />
            <rect x="210" y="130" width="3.5" height="3.5" fill="#D4BD7A" opacity="0.30" transform="rotate(45 212 132)" />
            {/* Small ring echo — bottom */}
            <ellipse cx="242" cy="180" rx="7" ry="7.5" stroke="#C4A090" strokeWidth="0.8" fill="none" opacity="0.15" />
            <polygon points="242,170 244,173 242,176 240,173" fill="#D4BD7A" opacity="0.20" />
          </svg>
        </>
      )}

      {/* ══════════════════════════════════════════════════════════
          ANNIVERSARY — Champagne flutes (cheers pose) + bubble sparkles
          ══════════════════════════════════════════════════════════ */}
      {dIsA && (
        <>
          {/* Warm rose glow */}
          <div className="absolute pointer-events-none rounded-full" style={{
            bottom: '10%', right: '-6%', width: '55%', height: '75%',
            background: 'radial-gradient(ellipse at 55% 60%, rgba(210,170,145,0.12) 0%, transparent 50%)',
          }} />
          {/* Champagne flutes — cheers pose */}
          <svg className="absolute pointer-events-none" style={isHome
            ? homeMotifStyle({ top: '20%', right: '8px', width: '95px', height: '95px', opacity: 0.52 })
            : { top: '20%', right: '8px', width: '95px', height: '95px', opacity: 0.52 }
          } viewBox="0 0 95 95">
            {/* Left flute — tilted left */}
            <g transform="rotate(-12, 35, 50)">
              {/* Bowl */}
              <path d="M28 18 Q28 35 35 42 Q42 35 42 18 Z" fill="#D4BD7A" opacity="0.35" />
              <path d="M28 18 Q28 35 35 42 Q42 35 42 18" stroke="#C9AD6A" strokeWidth="1.5" fill="none" />
              {/* Rim highlight */}
              <path d="M30 19 Q35 16 40 19" stroke="#E8D8A0" strokeWidth="1" fill="none" opacity="0.7" />
              {/* Stem */}
              <line x1="35" y1="42" x2="35" y2="68" stroke="#C9AD6A" strokeWidth="1.5" />
              {/* Base */}
              <ellipse cx="35" cy="70" rx="8" ry="2.5" stroke="#C9AD6A" strokeWidth="1.2" fill="none" />
              {/* Bubbles inside */}
              <circle cx="33" cy="28" r="1.2" fill="#E8D8A0" opacity="0.6" />
              <circle cx="37" cy="24" r="0.8" fill="#E8D8A0" opacity="0.5" />
              <circle cx="35" cy="32" r="1" fill="#E8D8A0" opacity="0.45" />
            </g>
            {/* Right flute — tilted right */}
            <g transform="rotate(12, 60, 50)">
              <path d="M53 18 Q53 35 60 42 Q67 35 67 18 Z" fill="#D4BD7A" opacity="0.35" />
              <path d="M53 18 Q53 35 60 42 Q67 35 67 18" stroke="#C9AD6A" strokeWidth="1.5" fill="none" />
              <path d="M55 19 Q60 16 65 19" stroke="#E8D8A0" strokeWidth="1" fill="none" opacity="0.7" />
              <line x1="60" y1="42" x2="60" y2="68" stroke="#C9AD6A" strokeWidth="1.5" />
              <ellipse cx="60" cy="70" rx="8" ry="2.5" stroke="#C9AD6A" strokeWidth="1.2" fill="none" />
              <circle cx="58" cy="28" r="1.2" fill="#E8D8A0" opacity="0.6" />
              <circle cx="62" cy="24" r="0.8" fill="#E8D8A0" opacity="0.5" />
              <circle cx="60" cy="32" r="1" fill="#E8D8A0" opacity="0.45" />
            </g>
            {/* Clink sparkle */}
            <circle cx="47" cy="12" r="2" fill="#D4BD7A" opacity="0.65" />
            <line x1="47" y1="6" x2="47" y2="3" stroke="#D4BD7A" strokeWidth="0.8" opacity="0.5" strokeLinecap="round" />
            <line x1="52" y1="8" x2="55" y2="5" stroke="#D4BD7A" strokeWidth="0.8" opacity="0.45" strokeLinecap="round" />
            <line x1="42" y1="8" x2="39" y2="5" stroke="#D4BD7A" strokeWidth="0.8" opacity="0.45" strokeLinecap="round" />
          </svg>
          {/* Rising bubble sparkles + rose-gold scatter */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 300 220" preserveAspectRatio="none">
            {/* Rising bubbles from flute area */}
            <circle cx="220" cy="18" r="2.2" fill="#D4BD7A" opacity="0.40" {...sparkleProps(0, 0.40)} />
            <circle cx="235" cy="8" r="1.5" fill="#E8D8A0" opacity="0.35" {...sparkleProps(1, 0.35)} />
            <circle cx="212" cy="5" r="1.8" fill="#D4BD7A" opacity="0.30" {...sparkleProps(2, 0.30)} />
            {/* Rose gold scatter */}
            <circle cx="250" cy="60" r="2" fill="#D4A894" opacity="0.40" {...sparkleProps(3, 0.40)} />
            <circle cx="268" cy="38" r="1.8" fill="#C49584" opacity="0.35" {...sparkleProps(4, 0.35)} />
            <circle cx="230" cy="100" r="2.2" fill="#D4A894" opacity="0.32" {...sparkleProps(5, 0.32)} />
            <circle cx="260" cy="125" r="2" fill="#C49584" opacity="0.30" {...sparkleProps(6, 0.30)} />
            <circle cx="205" cy="145" r="1.8" fill="#D4A894" opacity="0.25" />
            <rect x="240" y="48" width="3.5" height="3.5" fill="#D4BD7A" opacity="0.35" transform="rotate(45 242 50)" />
            <rect x="218" y="115" width="3" height="3" fill="#D4A894" opacity="0.28" transform="rotate(45 220 117)" />
            {/* Small champagne echo — bottom */}
            <path d="M245 170 Q245 178 248 182" stroke="#C9AD6A" strokeWidth="0.7" fill="none" opacity="0.15" />
            <path d="M255 170 Q255 178 252 182" stroke="#C9AD6A" strokeWidth="0.7" fill="none" opacity="0.15" />
            <circle cx="250" cy="165" r="1.2" fill="#D4BD7A" opacity="0.18" />
          </svg>
        </>
      )}

      {/* ══════════════════════════════════════════════════════════
          BIRTHDAY — Gold/green balloons + classic confetti
          ══════════════════════════════════════════════════════════ */}
      {dIsBirthday && (
        <>
          {/* Gold balloon — top right */}
          <svg className="absolute pointer-events-none" style={isHome
            ? homeMotifStyle({ top: '-18px', right: '-10px', width: '90px', height: '150px', opacity: dGoldOp })
            : { top: '-18px', right: '-10px', width: '90px', height: '150px', opacity: dGoldOp }
          } viewBox="0 0 90 150">
            <defs>
              <radialGradient id={`gB1-${eventId}`} cx="38%" cy="28%">
                <stop offset="0%" stopColor="#EFE0B0" />
                <stop offset="35%" stopColor="#D4B968" />
                <stop offset="65%" stopColor="#C0A050" />
                <stop offset="100%" stopColor="#A88E40" />
              </radialGradient>
            </defs>
            <ellipse cx="45" cy="42" rx="36" ry="40" fill={`url(#gB1-${eventId})`} />
            <ellipse cx="32" cy="26" rx="10" ry="14" fill="white" opacity="0.28" />
            <path d="M45 82 Q47 95 45 110 Q43 125 45 140" stroke="#B8A060" strokeWidth="0.8" fill="none" opacity="0.4" />
          </svg>
          {/* Green balloon — top left */}
          {dGreenOp > 0 && (
            <svg className="absolute pointer-events-none" style={isHome
              ? homeMotifLeftStyle({ top: '-20px', left: '-15px', width: '80px', height: '135px', opacity: dGreenOp })
              : { top: '-20px', left: '-15px', width: '80px', height: '135px', opacity: dGreenOp }
            } viewBox="0 0 80 135">
              <ellipse cx="40" cy="38" rx="32" ry="36" fill="#C8E0BE" />
              <ellipse cx="28" cy="24" rx="8" ry="12" fill="white" opacity="0.25" />
              <path d="M40 74 Q42 88 40 105 Q38 118 40 130" stroke="#98B48E" strokeWidth="0.7" fill="none" opacity="0.35" />
            </svg>
          )}
          {/* Confetti scatter */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 300 220" preserveAspectRatio="none">
            <circle cx="200" cy="30" r="2.5" fill="#C9AD6A" opacity={0.55 * dConfetti} {...sparkleProps(0, 0.55 * dConfetti)} />
            <circle cx="235" cy="55" r="2" fill="#D4BD7A" opacity={0.50 * dConfetti} {...sparkleProps(1, 0.50 * dConfetti)} />
            <circle cx="260" cy="35" r="2.5" fill="#C9AD6A" opacity={0.45 * dConfetti} {...sparkleProps(2, 0.45 * dConfetti)} />
            <circle cx="220" cy="85" r="2" fill="#D4BD7A" opacity={0.48 * dConfetti} {...sparkleProps(3, 0.48 * dConfetti)} />
            <circle cx="270" cy="72" r="2.8" fill="#C9AD6A" opacity={0.42 * dConfetti} {...sparkleProps(4, 0.42 * dConfetti)} />
            <circle cx="210" cy="120" r="2" fill="#D4BD7A" opacity={0.40 * dConfetti} {...sparkleProps(5, 0.40 * dConfetti)} />
            <circle cx="255" cy="110" r="2.2" fill="#C9AD6A" opacity={0.38 * dConfetti} {...sparkleProps(6, 0.38 * dConfetti)} />
            <circle cx="240" cy="150" r="2.5" fill="#D4BD7A" opacity={0.35 * dConfetti} />
            <circle cx="215" cy="55" r="2" fill="#98B48E" opacity={0.45 * dConfetti} />
            <circle cx="250" cy="90" r="1.8" fill="#A8C09E" opacity={0.40 * dConfetti} />
            <circle cx="195" cy="135" r="2" fill="#98B48E" opacity={0.35 * dConfetti} />
            <rect x="228" y="42" width="4" height="4" fill="#C9AD6A" opacity={0.48 * dConfetti} transform="rotate(45 230 44)" />
            <rect x="260" y="98" width="3.5" height="3.5" fill="#98B48E" opacity={0.38 * dConfetti} transform="rotate(45 262 100)" />
            <path d="M205 28 Q212 40 203 52" stroke="#C9AD6A" strokeWidth="1" fill="none" strokeLinecap="round" opacity={0.40 * dConfetti} />
            <path d="M265 55 Q270 66 262 78" stroke="#D4BD7A" strokeWidth="0.9" fill="none" strokeLinecap="round" opacity={0.35 * dConfetti} />
          </svg>
        </>
      )}

      {/* ══════════════════════════════════════════════════════════
          BABY SHOWER — Soft pastel balloon cluster
          ══════════════════════════════════════════════════════════ */}
      {dIsBs && (
        <>
          <svg className="absolute pointer-events-none" style={isHome
            ? homeMotifLeftStyle({ top: '-14px', left: '-8px', width: '85px', height: '140px', opacity: 0.68 })
            : { top: '-14px', left: '-8px', width: '85px', height: '140px', opacity: 0.68 }
          } viewBox="0 0 85 140">
            <defs><radialGradient id={`bsB1-${eventId}`} cx="38%" cy="28%"><stop offset="0%" stopColor="#FFF5E8" /><stop offset="40%" stopColor="#F0DCC4" /><stop offset="100%" stopColor="#D8C09E" /></radialGradient></defs>
            <ellipse cx="42" cy="38" rx="32" ry="36" fill={`url(#bsB1-${eventId})`} />
            <ellipse cx="30" cy="24" rx="9" ry="13" fill="white" opacity="0.25" />
            <path d="M42 74 Q44 88 42 105 Q40 118 42 135" stroke="#C8B490" strokeWidth="0.7" fill="none" opacity="0.35" />
          </svg>
          <svg className="absolute pointer-events-none" style={isHome
            ? homeMotifLeftStyle({ top: '5px', left: '45px', width: '65px', height: '115px', opacity: 0.50 })
            : { top: '5px', left: '45px', width: '65px', height: '115px', opacity: 0.50 }
          } viewBox="0 0 65 115">
            <defs><radialGradient id={`bsB2-${eventId}`} cx="40%" cy="30%"><stop offset="0%" stopColor="#E8F0E4" /><stop offset="40%" stopColor="#C8D8BE" /><stop offset="100%" stopColor="#A8B89E" /></radialGradient></defs>
            <ellipse cx="32" cy="32" rx="26" ry="30" fill={`url(#bsB2-${eventId})`} />
            <ellipse cx="22" cy="20" rx="7" ry="10" fill="white" opacity="0.22" />
            <path d="M32 62 Q34 76 32 92 Q30 105 32 112" stroke="#98B490" strokeWidth="0.6" fill="none" opacity="0.30" />
          </svg>
          <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 300 220" preserveAspectRatio="none">
            <circle cx="205" cy="35" r="2" fill="#E0CDB0" opacity="0.42" />
            <circle cx="240" cy="70" r="1.8" fill="#B8C8AE" opacity="0.38" />
            <circle cx="265" cy="45" r="2" fill="#E0C0B0" opacity="0.35" />
            <circle cx="225" cy="110" r="1.8" fill="#D8C8A8" opacity="0.32" />
            <circle cx="260" cy="130" r="2" fill="#B8C8AE" opacity="0.28" />
          </svg>
        </>
      )}

      {/* ══════════════════════════════════════════════════════════
          GRADUATION — Mortarboard cap + purple/gold confetti
          ══════════════════════════════════════════════════════════ */}
      {dIsGr && (
        <>
          <div className="absolute pointer-events-none rounded-full" style={{
            top: '3%', right: '-4%', width: '48%', height: '62%',
            background: 'radial-gradient(ellipse at 55% 40%, rgba(140,120,180,0.10) 0%, transparent 50%)',
          }} />
          <svg className="absolute pointer-events-none" style={isHome
            ? homeMotifStyle({ top: '16%', right: '6px', width: '80px', height: '65px', opacity: 0.58 })
            : { top: '16%', right: '6px', width: '80px', height: '65px', opacity: 0.58 }
          } viewBox="0 0 110 90">
            <polygon points="55,6 105,30 55,48 5,30" fill="#3D3558" opacity="0.85" />
            <path d="M22 30 Q22 50 55 50 Q88 50 88 30" fill="#2C2345" opacity="0.70" />
            <line x1="80" y1="30" x2="88" y2="30" stroke="#C9AD6A" strokeWidth="1.2" opacity="0.65" />
            <path d="M88 30 Q90 45 88 58 Q86 68 85 78" stroke="#C9AD6A" strokeWidth="1.3" fill="none" strokeLinecap="round" opacity="0.55" />
            <circle cx="85" cy="80" r="3.5" fill="#C9AD6A" opacity="0.50" />
          </svg>
          <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 300 220" preserveAspectRatio="none">
            <circle cx="200" cy="32" r="2.5" fill="#C9AD6A" opacity="0.50" {...sparkleProps(0, 0.50)} />
            <circle cx="235" cy="58" r="2" fill="#B0A0C8" opacity="0.45" {...sparkleProps(1, 0.45)} />
            <circle cx="262" cy="38" r="2.2" fill="#D4BD7A" opacity="0.42" {...sparkleProps(2, 0.42)} />
            <circle cx="215" cy="90" r="1.8" fill="#A898B8" opacity="0.40" {...sparkleProps(3, 0.40)} />
            <circle cx="275" cy="70" r="2.5" fill="#C9AD6A" opacity="0.38" {...sparkleProps(4, 0.38)} />
            <circle cx="248" cy="120" r="2" fill="#B0A0C8" opacity="0.35" {...sparkleProps(5, 0.35)} />
            <circle cx="225" cy="145" r="2.2" fill="#D4BD7A" opacity="0.32" {...sparkleProps(6, 0.32)} />
            <rect x="230" y="48" width="4" height="4" fill="#C9AD6A" opacity="0.40" transform="rotate(45 232 50)" />
            <rect x="260" y="95" width="3.5" height="3.5" fill="#B0A0C8" opacity="0.35" transform="rotate(45 262 97)" />
            <path d="M210 28 Q218 40 208 52" stroke="#C9AD6A" strokeWidth="1" fill="none" strokeLinecap="round" opacity="0.38" />
            <path d="M268 50 Q274 62 264 74" stroke="#B0A0C8" strokeWidth="0.9" fill="none" strokeLinecap="round" opacity="0.32" />
          </svg>
        </>
      )}

      {/* ══════════════════════════════════════════════════════════
          HOUSEWARMING — House silhouette + warm dots
          ══════════════════════════════════════════════════════════ */}
      {dIsHw && (
        <>
          <div className="absolute pointer-events-none rounded-full" style={{
            top: '8%', right: '-4%', width: '42%', height: '55%',
            background: 'radial-gradient(ellipse at 55% 40%, rgba(200,180,140,0.08) 0%, transparent 45%)',
          }} />
          <svg className="absolute pointer-events-none" style={isHome
            ? homeMotifStyle({ top: '22%', right: '8px', width: '65px', height: '58px', opacity: 0.48 })
            : { top: '22%', right: '8px', width: '65px', height: '58px', opacity: 0.48 }
          } viewBox="0 0 88 78">
            <path d="M44 6 L84 34 L4 34 Z" fill="#B8A080" />
            <rect x="14" y="34" width="60" height="38" fill="#B8A080" />
            <rect x="34" y="46" width="16" height="26" rx="2" fill="#A09070" />
            <rect x="20" y="44" width="10" height="10" rx="1" fill="#D0C0A0" opacity="0.55" />
            <rect x="56" y="44" width="10" height="10" rx="1" fill="#D0C0A0" opacity="0.55" />
            <rect x="62" y="12" width="9" height="22" fill="#B8A080" />
          </svg>
          <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 300 220" preserveAspectRatio="none">
            <circle cx="220" cy="40" r="2" fill="#C9AD6A" opacity="0.24" {...sparkleProps(0, 0.24)} />
            <circle cx="260" cy="75" r="1.8" fill="#D4BD7A" opacity="0.20" {...sparkleProps(1, 0.20)} />
            <circle cx="235" cy="120" r="2" fill="#C9AD6A" opacity="0.18" {...sparkleProps(2, 0.18)} />
            <circle cx="270" cy="110" r="1.5" fill="#D4BD7A" opacity="0.16" {...sparkleProps(3, 0.16)} />
          </svg>
        </>
      )}

      {/* ══════════════════════════════════════════════════════════
          HOLIDAY — Glow orbs + mixed confetti
          ══════════════════════════════════════════════════════════ */}
      {dIsHol && (
        <>
          <div className="absolute pointer-events-none" style={{ top: 0, right: 0, width: '55%', height: '100%' }}>
            <div className="absolute rounded-full" style={{
              top: '5%', right: '4%', width: '52px', height: '52px',
              background: 'radial-gradient(circle, rgba(255,220,160,0.45) 0%, transparent 60%)',
            }} />
            <div className="absolute rounded-full" style={{
              top: '30%', right: '20%', width: '40px', height: '40px',
              background: 'radial-gradient(circle, rgba(200,190,240,0.38) 0%, transparent 60%)',
            }} />
            <div className="absolute rounded-full" style={{
              top: '55%', right: '8%', width: '35px', height: '35px',
              background: 'radial-gradient(circle, rgba(255,200,140,0.35) 0%, transparent 60%)',
            }} />
          </div>
          <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 300 220" preserveAspectRatio="none">
            <circle cx="208" cy="35" r="2.5" fill="#D4BD7A" opacity="0.42" />
            <circle cx="240" cy="65" r="2" fill="#C9AD6A" opacity="0.38" />
            <circle cx="265" cy="40" r="2.2" fill="#D4BD7A" opacity="0.35" />
            <circle cx="222" cy="100" r="1.8" fill="#C9AD6A" opacity="0.40" />
            <circle cx="275" cy="78" r="2.5" fill="#D4BD7A" opacity="0.35" />
            <circle cx="225" cy="48" r="2" fill="#C0C0C8" opacity="0.35" />
            <circle cx="258" cy="85" r="1.8" fill="#B8B8C0" opacity="0.30" />
            <rect x="232" y="38" width="4" height="4" fill="#D4BD7A" opacity="0.38" transform="rotate(45 234 40)" />
            <rect x="270" cy="100" width="3.5" height="3.5" fill="#C0C0C8" opacity="0.30" transform="rotate(45 272 102)" />
            <path d="M212 30 Q220 42 210 54" stroke="#D4BD7A" strokeWidth="1" fill="none" strokeLinecap="round" opacity="0.38" />
            <path d="M262 55 Q268 66 258 78" stroke="#A898C0" strokeWidth="0.9" fill="none" strokeLinecap="round" opacity="0.30" />
          </svg>
        </>
      )}
    </>
  );
}
